import { styled } from "styled-components";

import { Image, UIFollowButton } from "common/components";
import type { ImageCollection } from "common/types/imageCollection";
import { useTranslate } from "common/utilities/useTranslate";
import { interpolateString } from "domains/language/interpolate";
import { useTopicFollowing } from "domains/topic/components/useTopicFollowing";

const StyledContainer = styled.div`
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
  font-weight: 500;
`;

const StyledImage = styled(Image)`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-right: 15px;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

type ManageTopicItemProps = {
  id: string;
  images?: ImageCollection | null;
  isFollowing: boolean;
  isFollowingUpdating: boolean | undefined;
  name: string;
};

export const ManageTopicItem = ({ id, name, images, isFollowing }: ManageTopicItemProps) => {
  const { isFollowingUpdating, toggleFollow } = useTopicFollowing({
    topicId: id,
    topicName: name,
    isFollowing,
    pageForTracking: "Monitor",
  });

  const translate = useTranslate();

  const followTopicAriaText = interpolateString(translate("app.monitor.manage_topic.follow"), {
    topicName: () => name,
  });
  const unfollowTopicAriaText = interpolateString(translate("app.monitor.manage_topic.unfollow"), {
    topicName: () => name,
  });

  return (
    <StyledContainer>
      <StyledRow>
        <StyledImage src={images?.x_small} alt={name} />
        <StyledText>{name}</StyledText>
      </StyledRow>
      <UIFollowButton
        isFollowing={isFollowing}
        isLoading={isFollowingUpdating}
        onClick={toggleFollow}
        title={isFollowing ? unfollowTopicAriaText : followTopicAriaText}
        variant="collapsed"
      />
    </StyledContainer>
  );
};
